import { redirectToContractSales, redirectToPlan } from 'businessCommon/src/services';
import { _lang, commonLang, isSystem } from '@umu/ukit-lang';
import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { DialogTips } from './dialogTips/dialogTips';
import { IPlanInfo as IPlan, PLAN_TYPE, VERSION_TYPE, IUserInfo } from '@umu/umu-types';
import { Toast } from 'common/src/services';
import { handlePromiseError } from 'common/utils/errorHandler';
import { Button, If, CustomDialog } from 'common/src/components';
import { setSwitchVersion } from 'plan/@planPricingCommon/service/switchVersion';

import { ACTION_TYPE, DEVICE_FROM_MAP } from '@umu/umu-api/src/services/payment/paymentEvent';
import { trackEvent } from '../utils/util';
import { setStatLogDialogs } from '@umu/umu-api';
import { applyAppPurchase } from 'plan/@planPricingCommon/utils/util';
import { $window } from '@umu/umu-dom';
interface IPlanInfo extends IPlan {
    /**
     * 使用账户数
     */
    used_account_quota?: number;
    /**
     * 主账号信息
     */
    enterprise_main_account?: string;
}
interface IProps {
    isRegisterUser: boolean;
    planInfo: IPlanInfo;
    siteHost: string;
    userInfo?: IUserInfo;
    platform: 'twww' | 'mobile';
    className?: string;
}

export const BasicBuyButton = (props: IProps) => {
    const { isRegisterUser, planInfo, platform, className = '' } = props;

    const hasPriceButton = useMemo(() => {
        return !isRegisterUser || (isRegisterUser && planInfo.plan_id === PLAN_TYPE.BASIC);
    }, [isRegisterUser, planInfo]);

    if (!hasPriceButton) {
        return null;
    }

    const onPlan = (planId: number, isBuyNow?: boolean) => {
        redirectToPlan(platform, isRegisterUser, planId, isBuyNow);
    };

    return (
        <div className={`price-button ${className}`}>
            <If condition={!isRegisterUser}>
                <Button onClick={() => onPlan(PLAN_TYPE.BASIC)} className="price-edition-btn" alias="striking">
                    {commonLang('免费注册')}
                </Button>
            </If>
            <If condition={isRegisterUser && planInfo.plan_id === 1}>
                <Button className="price-edition-btn" alias="striking" onClick={() => onPlan(PLAN_TYPE.BASIC)}>
                    {commonLang('正在使用')}
                </Button>
            </If>
        </div>
    );
};

export const EnterpriseBuyButton = (props: IProps) => {
    const { isRegisterUser, planInfo, platform, className = '' } = props;

    const hasPriceButton = useMemo(() => {
        return !isRegisterUser || planInfo.plan_id <= PLAN_TYPE.INSTITUTE;
    }, [isRegisterUser, planInfo]);

    if (!hasPriceButton) {
        return null;
    }

    return (
        <div className={`price-button ${className}`}>
            <If condition={(isRegisterUser && planInfo.plan_id < 4) || !isRegisterUser}>
                <Button
                    className="price-edition-btn"
                    alias="striking"
                    onClick={() => {
                        setStatLogDialogs({ page_type: 16, action_type: '3' });
                        trackEvent(ACTION_TYPE.CLICK_CONTRACT, DEVICE_FROM_MAP[platform]);
                        redirectToContractSales(platform);
                    }}
                >
                    {commonLang('联系销售')}
                </Button>
            </If>
            <If condition={isRegisterUser && (planInfo.plan_id === 4 || planInfo.plan_id === 5)}>
                <Button className="price-edition-btn" alias="striking">
                    {commonLang('正在使用')}
                </Button>
            </If>
        </div>
    );
};
const onSwitchVersion = (accountQuota: number, accountMail: string) => {
    setSwitchVersion('2')
        .then((errorCode: string) => {
            if (!Number(errorCode)) {
                Toast.show(commonLang('完成版本切换，请重新登录'));
                $window.location.href = '/globaluser/loginpage';
            }
            if (Number(errorCode) === 20304) {
                CustomDialog({
                    className: 'switch-version-dialog-tips',
                    canClose: true,
                    width: '4rem',
                    childProps: {
                        title: commonLang('无法切换专业版'),
                        descList: [
                            commonLang(
                                '当前团队版还有其他${accountQuota}个账户正在使用中，无法切换专业版。请先到“管理平台 - 组织管理 - 账户管理”页面完成除“${accountMail}”主账户外其他账户的删除后，再来重试。',
                            )
                                .replace('${accountQuota}', accountQuota)
                                .replace('${accountMail}', accountMail),
                        ],

                        isShowCancel: false,
                        submitText: commonLang('知道了'),
                    },
                    contentComponent: DialogTips,
                });
            } else if (Number(errorCode) === 20305) {
                CustomDialog({
                    className: 'switch-version-dialog-tips',
                    canClose: true,
                    width: '4rem',
                    childProps: {
                        title: commonLang('无法切换专业版'),
                        descList: [commonLang('切换版本频率太高，请稍后再试。')],
                        isShowCancel: false,
                        submitText: commonLang('知道了'),
                    },
                    contentComponent: DialogTips,
                });
            } else if (Number(errorCode) === 20001) {
                CustomDialog({
                    className: 'switch-version-dialog-tips',
                    canClose: true,
                    width: '4rem',
                    childProps: {
                        title: commonLang('无法切换专业版'),
                        descList: [commonLang('提交数据有误')],
                        isShowCancel: false,
                        submitText: commonLang('知道了'),
                    },
                    contentComponent: DialogTips,
                });
            }
        })
        .catch((error: unknown) => {
            handlePromiseError(error, 'BuyButton.onSwitchVersion', 'BuyButton setSwitchVersion error', {
                accountQuota,
                accountMail,
                versionType: '2',
            });
        });
};
const onSwitchProfessionalVersion = (planInfo: IPlanInfo) => {
    const { expire_time, used_account_quota = 1, enterprise_main_account = '' } = planInfo;
    const expireTimeLabel = dayjs(expire_time * 1000).format('YYYY-MM-DD HH:mm:ss');
    CustomDialog({
        className: 'switch-version-dialog-tips',
        canClose: true,
        width: '4rem',
        childProps: {
            title: commonLang('确认切换专业版？'),
            descList: [
                commonLang('当前正在使用团队版，到期时间为${expireTime}，请确认是否切换为专业版？').replace(
                    '${expireTime}',
                    expireTimeLabel,
                ),
                commonLang('切换为专业版后将不再具有管理后台功能；专业版到期时间为 ${expireTime}。').replace(
                    '${expireTime}',
                    expireTimeLabel,
                ),
            ],

            isShowCancel: true,
            onSubmitClick: () => onSwitchVersion(used_account_quota - 1, enterprise_main_account),
            submitText: commonLang('切换'),
        },
        contentComponent: DialogTips,
    });
};
export const ProfessionalBuyButton = (props: IProps) => {
    const { isRegisterUser, planInfo, platform, className = '', userInfo } = props;
    // 是否为商业试用版 该版本下不展示立即购买
    const isBusinessTrial = Number(planInfo.teacher_status) === VERSION_TYPE.COMMERICAL_TRIAL;

    const hasPriceButton = useMemo(() => {
        return (
            !isRegisterUser ||
            (isRegisterUser && planInfo.plan_id === PLAN_TYPE.BASIC && !isBusinessTrial) ||
            (isRegisterUser && planInfo.plan_id === PLAN_TYPE.PROFESSIONAL)
        );
    }, [isRegisterUser, planInfo, isBusinessTrial]);

    if (!hasPriceButton) {
        return null;
    }

    const onPlan = (planId: number, isBuyNow?: boolean) => {
        if (applyAppPurchase(2)) {
            return;
        }
        redirectToPlan(platform, isRegisterUser, planId, isBuyNow);
    };

    const renderSwitchButton = () => {
        const isCn = isSystem('CN');
        const accountQuota = planInfo.account_quota;
        const isInit = Number(accountQuota) === 10; // 账户个数为10的时候才展示切换专业版
        const isCommerical = planInfo.plan_id === 3; // 商业版
        const isAdmin = userInfo && userInfo.enterprise_info && userInfo.enterprise_info.role_type === 4; // 管理员
        const version = userInfo && userInfo.enterprise_info ? userInfo && userInfo.enterprise_info.version_type : '0'; // 获取版本
        const isTeam = Number(version) === 9; // 商业版
        const isExpire = Number(planInfo.expire_days) === 0;
        const isShow = isCn && isCommerical && isInit && isAdmin && isTeam && !isExpire;

        return isShow ? (
            <Button
                className="price-edition-btn"
                alias="striking"
                onClick={() => onSwitchProfessionalVersion(planInfo)}
            >
                {commonLang('切换专业版')}
            </Button>
        ) : null;
    };

    // console.log(' isBusinessTrial = ', isBusinessTrial);
    return (
        <div className={`price-button ${className}`}>
            <If condition={!isRegisterUser}>
                <Button
                    className="price-edition-btn"
                    alias="striking"
                    onClick={() => {
                        trackEvent(ACTION_TYPE.CLICK_PROFESSION, DEVICE_FROM_MAP[platform]);
                        onPlan(PLAN_TYPE.PROFESSIONAL, true);
                    }}
                >
                    {commonLang('立即购买')}
                </Button>
            </If>
            <If condition={isRegisterUser && planInfo.plan_id === 1 && !isBusinessTrial}>
                <Button
                    className="price-edition-btn"
                    alias="striking"
                    onClick={() => {
                        trackEvent(ACTION_TYPE.CLICK_PROFESSION, DEVICE_FROM_MAP[platform]);
                        onPlan(PLAN_TYPE.PROFESSIONAL, true);
                    }}
                >
                    {commonLang('立即购买')}
                </Button>
            </If>
            <If condition={isRegisterUser && planInfo.plan_id === 2}>
                <Button
                    className="price-edition-btn"
                    alias="striking"
                    onClick={() => {
                        onPlan(PLAN_TYPE.PROFESSIONAL, true);
                    }}
                >
                    {commonLang('正在使用')}
                </Button>
            </If>
            {renderSwitchButton()}
        </div>
    );
};
